import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/store';
import { Wifi, Plus, Loader2, Copy, CheckCircle2, Clock } from 'lucide-react';

const brl = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

export default function ConnectionSlotsCard() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pix, setPix] = useState(null);      // { qr_code, qr_code_text, id }
  const [buying, setBuying] = useState(false);
  const [polling, setPolling] = useState(false);

  const load = async () => {
    try { const r = await api.get('/connections/billing'); setInfo(r.data); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const comprar = async (slotId) => {
    setBuying(true);
    try {
      const r = await api.post('/connections/slots/purchase', slotId ? { slot_id: slotId } : {});
      setPix(r.data);
      pollPayment(r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao gerar pagamento'); }
    finally { setBuying(false); }
  };

  const pollPayment = (txId) => {
    setPolling(true);
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/connections/slots/payment/${txId}`);
        if (r.data.status === 'approved') {
          clearInterval(t); setPolling(false); setPix(null);
          toast.success('Conexão extra ativada! 🎉');
          load();
        }
      } catch { clearInterval(t); setPolling(false); }
    }, 4000);
    setTimeout(() => { clearInterval(t); setPolling(false); }, 300000);
  };

  if (loading || !info) return null;
  // rede desativou a compra e o revenda não tem extras ativas → não mostra o card
  if (info.rede_desativou && !(info.extras_ativas > 0) && !(info.extras && info.extras.length > 0)) return null;

  const dias = (exp) => {
    try { return Math.max(0, Math.ceil((new Date(exp) - new Date()) / 86400000)); } catch { return 0; }
  };

  return (
    <Card className="border-cyan-500/20 bg-gradient-to-br from-cyan-500/[0.04] to-transparent overflow-hidden relative">
      <div className="pointer-events-none absolute -top-12 -right-12 w-40 h-40 rounded-full bg-cyan-500/10 blur-3xl" />
      <CardContent className="p-5 relative">
        <div className="flex items-center gap-2.5 mb-1">
          <div className="w-9 h-9 rounded-xl bg-cyan-500/15 text-cyan-400 flex items-center justify-center"><Wifi className="w-4 h-4" /></div>
          <div>
            <h3 className="font-semibold leading-tight">Conexões extras</h3>
            <p className="text-[11px] text-muted-foreground">Amplie seu limite de WhatsApp</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Seu plano inclui {info.base === -1 ? 'conexões ilimitadas' : `${info.base} conexão(ões)`}. Compre conexões extras por {brl(info.price)} cada (30 dias, máx {info.max}). Se sua conta vencer, as extras ficam desconectadas (como a conexão padrão) e voltam ao renovar.
        </p>

        {/* conexões extras ativas */}
        {info.extras && info.extras.length > 0 && (
          <div className="space-y-1.5 mb-3">
            {info.extras.map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-lg border border-border/50 px-3 py-2">
                <span className="text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500" /> Conexão extra</span>
                <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> {dias(s.expires_at)} dias
                  <Button size="sm" variant="outline" className="h-7 ml-2" onClick={() => comprar(s.id)} disabled={buying}>Renovar</Button>
                </span>
              </div>
            ))}
          </div>
        )}

        {/* PIX gerado */}
        {pix ? (
          <div className="rounded-xl border border-cyan-500/25 bg-cyan-500/[0.04] p-4 text-center">
            <p className="text-sm font-medium mb-2">Pague {brl(info.price)} via PIX para ativar</p>
            {pix.qr_code && <img src={pix.qr_code.startsWith('data:') ? pix.qr_code : `data:image/png;base64,${pix.qr_code}`} alt="QR" className="w-44 h-44 mx-auto rounded-lg bg-white p-2" />}
            <div className="flex gap-2 mt-3">
              <input readOnly value={pix.qr_code_text} className="flex-1 bg-muted/40 border border-border/60 rounded-lg px-2 py-1.5 text-xs font-mono truncate" />
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(pix.qr_code_text); toast.success('Copiado!'); }}><Copy className="w-4 h-4" /></Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2 flex items-center justify-center gap-1.5">
              {polling && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Aguardando pagamento…
            </p>
            <button onClick={() => setPix(null)} className="text-xs text-muted-foreground underline mt-2">Cancelar</button>
          </div>
        ) : (
          info.pode_comprar ? (
            <Button onClick={() => comprar(null)} disabled={buying} className="w-full bg-gradient-to-r from-cyan-500 to-primary">
              {buying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Comprar conexão extra — {brl(info.price)}
            </Button>
          ) : (
            <p className="text-xs text-amber-500 text-center py-2">Você atingiu o limite de {info.max} conexões extras.</p>
          )
        )}
      </CardContent>
    </Card>
  );
}
